/**
 * Pure helpers extracted from StorefrontSavedAddresses.vue so they can be
 * unit-tested under vitest (which can't mount Vuetify 3 SFCs in this project —
 * see project_vitest-no-plugin-vue.md).
 *
 * Backend contract (PR #992 / api/src/models/address.py):
 *   - Fields: id, account_id, label, recipient_name, street_and_number,
 *     suite_unit_etc, city, region, zip_code, country (ISO-2),
 *     is_default, created_at, modified_at
 *   - On POST: the server DERIVES account_id from the authenticated
 *     session for every role and IGNORES any client-supplied value.
 *     Do NOT add account_id to WRITABLE_FIELDS below to "help" -- the
 *     server would drop it anyway, and sending it would misrepresent
 *     this client as choosing the owner. (Until 2026-08-28 the server
 *     only derived it for the Shopper role, so every other role's save
 *     died on a NOT NULL violation surfaced as a 500.)
 *   - On PATCH as Shopper: server strips account_id
 *   - Single is_default=true invariant: enforced by partial unique index;
 *     server demotes any prior default on POST/PATCH that sets is_default
 */

const REQUIRED_FIELDS = [
  "recipient_name",
  "street_and_number",
  "city",
  "region",
  "zip_code",
  "country",
];

const WRITABLE_FIELDS = [
  "label",
  "recipient_name",
  "street_and_number",
  "suite_unit_etc",
  "city",
  "region",
  "zip_code",
  "country",
  "is_default",
];

/**
 * Format an Address into a single-line display string for list rendering.
 *
 * Pattern: "{recipient}, {street}[, {suite}], {city}, {region} {zip}, {country}"
 * Label is intentionally NOT included — the UI renders the label as a chip /
 * title separately from the address line.
 *
 * @param {object|null|undefined} address
 * @returns {string}
 */
export function formatAddressLine(address) {
  if (!address || typeof address !== "object") return "";
  const parts = [];
  if (address.recipient_name) parts.push(address.recipient_name);
  if (address.street_and_number) parts.push(address.street_and_number);
  if (address.suite_unit_etc) parts.push(address.suite_unit_etc);
  if (address.city && address.region && address.zip_code) {
    parts.push(`${address.city}, ${address.region} ${address.zip_code}`);
  } else {
    if (address.city) parts.push(address.city);
    if (address.region && address.zip_code) {
      parts.push(`${address.region} ${address.zip_code}`);
    } else if (address.region) {
      parts.push(address.region);
    } else if (address.zip_code) {
      parts.push(address.zip_code);
    }
  }
  if (address.country) parts.push(address.country);
  return parts.join(", ");
}

/**
 * Sort addresses with the default first, then by modified_at descending.
 * Addresses missing modified_at sort to the end of the non-default group.
 *
 * Does NOT mutate the input.
 *
 * @param {Array} addresses
 * @returns {Array}
 */
export function sortAddressesDefaultFirst(addresses) {
  const list = Array.isArray(addresses) ? addresses.slice() : [];
  return list
    .map((address, index) => ({ address, index }))
    .sort((a, b) => {
      const aDefault = !!(a.address && a.address.is_default);
      const bDefault = !!(b.address && b.address.is_default);
      if (aDefault && !bDefault) return -1;
      if (!aDefault && bDefault) return 1;
      // Tie-break: modified_at descending; missing → end
      const aTime =
        a.address && a.address.modified_at
          ? Date.parse(a.address.modified_at)
          : NaN;
      const bTime =
        b.address && b.address.modified_at
          ? Date.parse(b.address.modified_at)
          : NaN;
      const aMissing = Number.isNaN(aTime);
      const bMissing = Number.isNaN(bTime);
      if (aMissing && bMissing) return a.index - b.index;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (bTime !== aTime) return bTime - aTime;
      return a.index - b.index;
    })
    .map((wrapped) => wrapped.address);
}

/**
 * Build a POST/PATCH payload from a form object.
 *
 * Strips fields the server controls (id, account_id, created_at,
 * modified_at) and jsonapi-vuex metadata (_jv). Normalizes country to
 * uppercase (defensive — server also normalizes, but this keeps the
 * payload clean and matches what validateAddressForm checks).
 *
 * @param {object|null|undefined} formData
 * @returns {object} payload safe to POST/PATCH
 */
export function buildAddressPayload(formData) {
  if (!formData || typeof formData !== "object") return {};
  const payload = {};
  for (const key of WRITABLE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(formData, key)) {
      payload[key] = formData[key];
    }
  }
  if (typeof payload.country === "string") {
    payload.country = payload.country.toUpperCase();
  }
  return payload;
}

/**
 * Validate an address form's contents.
 *
 * Returns { valid: boolean, errors: { [field]: string } }. Missing/whitespace
 * required fields produce per-field error messages. Country must be a
 * 2-character string (ISO-2). Label and suite_unit_etc are optional.
 *
 * @param {object|null|undefined} formData
 * @returns {{valid: boolean, errors: object}}
 */
export function validateAddressForm(formData) {
  const errors = {};
  if (!formData || typeof formData !== "object") {
    return { valid: false, errors: { _form: "Form data is required" } };
  }
  for (const field of REQUIRED_FIELDS) {
    const value = formData[field];
    if (typeof value !== "string" || value.trim() === "") {
      errors[field] = `${humanLabel(field)} is required`;
    }
  }
  // Country additionally must be exactly 2 chars (ISO-2)
  if (!errors.country) {
    if (typeof formData.country !== "string") {
      errors.country = "Country must be a 2-character code";
    } else if (formData.country.trim().length !== 2) {
      errors.country = "Country must be a 2-character ISO code (e.g., US)";
    }
  } else if (typeof formData.country !== "string") {
    // Override the "required" message — non-string is a type problem, not absence.
    errors.country = "Country must be a 2-character code";
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

function humanLabel(field) {
  switch (field) {
    case "recipient_name":
      return "Recipient name";
    case "street_and_number":
      return "Street address";
    case "city":
      return "City";
    case "region":
      return "State / region";
    case "zip_code":
      return "ZIP / postal code";
    case "country":
      return "Country";
    default:
      return field;
  }
}
